import { useState } from "react";
import { Button } from "@/components/ui/button-variants";
import { Check, Upload, ChevronRight, UserPlus, Sparkles, FileText, Loader2, CheckCircle2, Clock, AlertTriangle, BadgeCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";
import { z } from "zod";
import { HoneypotField } from "@/components/security/HoneypotField";
import { useRateLimiter } from "@/hooks/useRateLimiter";
import { TurnstileCaptcha } from "@/components/security/TurnstileCaptcha";
import { trackFormSubmission as trackAgencyFormSubmission } from "@/lib/trackFormSubmission";

// Schéma de validation sécurisé
const candidatureSchema = z.object({
  firstName: z.string()
    .trim()
    .min(2, "Le prénom doit contenir au moins 2 caractères")
    .max(100, "Le prénom est trop long")
    .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, "Le prénom contient des caractères invalides"),
  lastName: z.string()
    .trim()
    .min(2, "Le nom doit contenir au moins 2 caractères")
    .max(100, "Le nom est trop long")
    .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, "Le nom contient des caractères invalides"),
  email: z.string()
    .trim()
    .email("Email invalide")
    .max(255, "Email trop long"),
  phone: z.string()
    .trim()
    .regex(/^[0-9\s]{10,14}$/, "Numéro de téléphone invalide"),
  specialty: z.string()
    .trim()
    .min(1, "La spécialité est requise")
    .max(200, "Spécialité trop longue"),
  zone: z.string()
    .trim()
    .max(500, "Zone trop longue")
    .optional(),
  availability: z.string()
    .trim()
    .max(1000, "Disponibilités trop longues")
    .optional(),
});

const SpontaneousApplication = () => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isBotDetected, setIsBotDetected] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    countryCode: "+33",
    specialty: "",
    zone: "",
    availability: "",
    cv: null as File | null,
    cvName: "",
    consent: false,
  });

  // Rate limiting: max 3 soumissions par 15 minutes
  const { checkLimit, isBlocked } = useRateLimiter({
    maxAttempts: 3,
    windowMs: 15 * 60 * 1000, // 15 minutes
    onLimitExceeded: () => {
      toast({
        title: "Trop de tentatives",
        description: "Veuillez patienter 15 minutes avant de réessayer",
        variant: "destructive"
      });
    }
  });

  const handleBotDetected = () => {
    setIsBotDetected(true);
    toast({
      title: "Action suspecte détectée",
      description: "Cette soumission a été bloquée pour des raisons de sécurité",
      variant: "destructive"
    });
  };


  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone: string) => {
    // Permet les formats français: 06 12 34 56 78, 0612345678, etc.
    return /^[0-9\s]{10,14}$/.test(phone.replace(/\s/g, ''));
  };

  const handleNextStep = () => {
    if (step === 1) {
      // Validation avec Zod pour la sécurité
      const validationResult = candidatureSchema.pick({
        firstName: true,
        lastName: true,
        email: true,
        phone: true
      }).safeParse(formData);

      if (!validationResult.success) {
        const firstError = validationResult.error.errors[0];
        toast({
          title: "Erreur de validation",
          description: firstError.message,
          variant: "destructive"
        });
        return;
      }
    }
    if (step < 3) setStep(step + 1);
  };

  const handlePreviousStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérifier le type de fichier
    if (file.type !== 'application/pdf') {
      toast({
        title: "Erreur",
        description: "Seuls les fichiers PDF sont acceptés",
        variant: "destructive"
      });
      return;
    }

    // Vérifier la taille (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Erreur",
        description: "Le fichier ne doit pas dépasser 10MB",
        variant: "destructive"
      });
      return;
    }

    setFormData({ ...formData, cv: file, cvName: file.name });
  };

  const handleSubmit = async () => {
    if (!isCaptchaVerified) {
      toast({
        title: "CAPTCHA requis",
        description: "Veuillez valider le CAPTCHA avant de soumettre",
        variant: "destructive"
      });
      return;
    }

    // Vérification bot
    if (isBotDetected) {
      toast({
        title: "Action bloquée",
        description: "Cette soumission ne peut pas être traitée",
        variant: "destructive"
      });
      return;
    }

    // Vérification rate limiting
    if (!checkLimit()) {
      return;
    }

    // Validation complète avec Zod
    const validationResult = candidatureSchema.safeParse(formData);

    if (!validationResult.success) {
      const firstError = validationResult.error.errors[0];
      toast({
        title: "Erreur de validation",
        description: firstError.message,
        variant: "destructive"
      });
      return;
    }

    if (!formData.cv) {
      toast({
        title: "Erreur",
        description: "Veuillez télécharger votre CV",
        variant: "destructive"
      });
      return;
    }

    if (!formData.consent) {
      toast({
        title: "Erreur",
        description: "Veuillez accepter la politique de confidentialité",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Étape 1: Validation (10%)
      setUploadProgress(10);
      await new Promise(resolve => setTimeout(resolve, 200));

      // Étape 2: Préparation de l'upload (30%)
      setUploadProgress(30);
      const timestamp = Date.now();
      const fileName = `cv/${timestamp}_${formData.firstName}_${formData.lastName}.pdf`;
      
      // Étape 3: Upload du fichier (70%)
      setUploadProgress(50);
      const { error: uploadError } = await supabase.storage
        .from('candidatures-spontanees')
        .upload(fileName, formData.cv);

      if (uploadError) throw uploadError;

      // Étape 4: Création d'une signed URL (valable 30 jours)
      setUploadProgress(70);
      const { data: signedUrlData } = await supabase.storage
        .from('candidatures-spontanees')
        .createSignedUrl(fileName, 60 * 60 * 24 * 30);

      // Étape 4.5: Insertion dans la base de données
      setUploadProgress(75);
      const { error: insertError } = await supabase
        .from('candidatures')
        .insert({
          job_id: 'spontanee',
          job_title: 'Candidature spontanée',
          job_location: formData.zone || 'France',
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: `${formData.countryCode}${formData.phone}`,
          specialty: formData.specialty,
          availability: formData.availability || 'Non spécifiée',
          cv_url: signedUrlData?.signedUrl || '',
          status: 'nouvelle'
        });

      if (insertError) {
        console.error('Erreur insertion candidature:', insertError);
        throw new Error("Échec de l'enregistrement de la candidature");
      }

      // Étape 5: Envoi de l'email de notification
      setUploadProgress(85);
      const { error: emailError } = await supabase.functions.invoke('send-candidature-email', {
        body: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          countryCode: formData.countryCode,
          specialty: formData.specialty,
          zone: formData.zone || 'Non spécifiée',
          availability: formData.availability || 'Non spécifiée',
          cvUrl: signedUrlData?.signedUrl || '',
        },
      });

      if (emailError) {
        console.error('Erreur envoi email:', emailError);
        // Continue même si l'email échoue
      }

      // Étape 6: Finalisation (100%)
      setUploadProgress(100);
      await new Promise(resolve => setTimeout(resolve, 500));

      trackAgencyFormSubmission("candidature_spontanee");
      const { trackFormSubmission } = await import("@/lib/gtmTracking");
      trackFormSubmission("candidature_spontanee");

      toast({
        title: "✓ Candidature envoyée",
        description: "Nous vous contacterons dans les 24h",
      });

      setStep(3);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer votre candidature",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <section id="candidature-spontanee" className="py-16 md:py-24 bg-gradient-to-br from-primary/5 via-background to-secondary/5 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]"></div>
      <div className="absolute top-20 -right-20 w-96 h-96 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 -left-20 w-96 h-96 bg-gradient-to-tr from-secondary/10 to-transparent rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header with badge */}
          <div className="text-center mb-12 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-5 py-2 bg-white/80 backdrop-blur-sm rounded-full mb-6 border border-primary/10 shadow-lg">
              <UserPlus className="w-4 h-4 text-primary" />
              <span className="text-primary font-semibold text-sm tracking-wide">REJOIGNEZ-NOUS</span>
            </div>
            <h2 className="font-poppins font-black text-3xl md:text-5xl bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-4">
              Candidature Spontanée
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Rejoignez notre pool de médecins en 3 étapes simples
            </p>
          </div>

          {/* Main card with gradient background */}
          <div className="relative group animate-fade-in delay-300">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500"></div>
            
            <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 overflow-hidden">
              <div className="p-8 md:p-12">
                {/* Progress Steps */}
                <div className="flex justify-between mb-10">
                  {[1, 2, 3].map((s) => (
                    <div
                      key={s}
                      className={`flex items-center ${s < 3 ? "flex-1" : ""}`}
                    >
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-300 shadow-lg ${
                          step >= s
                            ? "bg-gradient-to-br from-primary to-secondary text-white scale-110"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {step > s ? <Check className="w-6 h-6" /> : s}
                      </div>
                      {s < 3 && (
                        <div
                          className={`flex-1 h-2 mx-3 rounded-full transition-all duration-300 ${
                            step > s ? "bg-gradient-to-r from-primary to-secondary" : "bg-muted"
                          }`}
                        />
                      )}
                    </div>
                  ))}
            </div>


                {/* Step Content */}
                <div className="bg-gradient-to-br from-background/50 to-primary/5 rounded-2xl p-6 md:p-8 border border-border/50">
                  {step === 1 && (
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                          <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="font-poppins font-bold text-xl text-foreground">
                          Étape 1 : Vos coordonnées
                        </h3>
                      </div>
                      
                      {/* Honeypot champ anti-bot */}
                      <HoneypotField onBot={handleBotDetected} />
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold mb-2 text-foreground">
                            Prénom *
                          </label>
                          <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) =>
                              setFormData({ ...formData, firstName: e.target.value })
                            }
                            className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-background"
                            placeholder="Jean"
                            required
                            maxLength={100}
                            disabled={isBlocked}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold mb-2 text-foreground">
                            Nom *
                          </label>
                          <input
                            type="text"
                            value={formData.lastName}
                            onChange={(e) =>
                              setFormData({ ...formData, lastName: e.target.value })
                            }
                            className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-background"
                            placeholder="Dupont"
                            required
                            maxLength={100}
                            disabled={isBlocked}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold mb-2 text-foreground">
                          Email *
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                          className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-background"
                          placeholder="docteur@example.com"
                          required
                          maxLength={255}
                          disabled={isBlocked}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold mb-2 text-foreground">
                          Téléphone *
                        </label>
                        <div className="flex gap-2">
                          <div className="px-4 py-3 border-2 border-border rounded-xl bg-muted/50 flex items-center gap-2 min-w-[100px]">
                            <span className="text-xl">🇫🇷</span>
                            <span className="font-medium text-foreground">+33</span>
                          </div>
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) =>
                              setFormData({ ...formData, phone: e.target.value })
                            }
                            className="flex-1 px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-background"
                            placeholder="06 12 34 56 78"
                            required
                            maxLength={14}
                            disabled={isBlocked}
                          />
                        </div>
                      </div>

                      <Button
                        variant="cta"
                        size="lg"
                        onClick={handleNextStep}
                        className="w-full group"
                        data-cta="candidature_spontanee_etape1"
                        data-cta-destination="step_2"
                      >
                        Continuer
                        <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  )}


                  {step === 2 && (
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                          <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="font-poppins font-bold text-xl text-foreground">
                          Étape 2 : Votre profil
                        </h3>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-foreground">
                          Spécialité *
                        </label>
                        <select
                          value={formData.specialty}
                          onChange={(e) =>
                            setFormData({ ...formData, specialty: e.target.value })
                          }
                          className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-background"
                          required
                        >
                          <option value="">Sélectionner une spécialité</option>
                          <option value="anesthesie">Anesthésie-Réanimation</option>
                          <option value="urgences">Médecine d'urgence</option>
                          <option value="radiologie">Radiologie</option>
                          <option value="geriatrie">Gériatrie</option>
                          <option value="generale">Médecine générale</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-foreground">
                          Zone géographique souhaitée
                        </label>
                        <input
                          type="text"
                          value={formData.zone}
                          onChange={(e) =>
                            setFormData({ ...formData, zone: e.target.value })
                          }
                          className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all bg-background"
                          placeholder="Ex: Île-de-France, Sud-Ouest..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-foreground">
                          Disponibilités
                        </label>
                        <textarea
                          value={formData.availability}
                          onChange={(e) =>
                            setFormData({ ...formData, availability: e.target.value })
                          }
                          className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all resize-none bg-background"
                          rows={3}
                          placeholder="Ex: Disponible immédiatement, 2 jours/semaine..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-foreground">
                          CV (PDF) *
                        </label>
                        {!formData.cvName ? (
                          <label htmlFor="cv-upload" className="block border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-primary hover:bg-primary/5 transition-all cursor-pointer group">
                            <Upload className="w-12 h-12 mx-auto mb-3 text-muted-foreground group-hover:text-primary transition-colors" />
                            <p className="text-sm font-medium text-foreground mb-1">
                              Cliquez pour uploader votre CV
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Format PDF uniquement (max 10MB)
                            </p>
                          </label>
                        ) : (
                          <div className="border-2 border-primary/30 rounded-xl p-4 bg-primary/5">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                                  <FileText className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-foreground">{formData.cvName}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {formData.cv ? `${(formData.cv.size / 1024 / 1024).toFixed(2)} MB` : ''}
                                  </p>
                                </div>
                              </div>
                              <label htmlFor="cv-upload">
                                <Button variant="outline" size="sm" type="button" asChild>
                                  <span className="cursor-pointer">Changer</span>
                                </Button>
                              </label>
                            </div>
                          </div>
                        )}
                        <input
                          id="cv-upload"
                          type="file"
                          accept=".pdf"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                      </div>
                      
                      {/* CAPTCHA Turnstile */}
                      <div className="border-2 border-primary/20 rounded-xl p-4 bg-gradient-to-br from-background to-primary/5">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-6 h-6 bg-gradient-primary rounded-full flex items-center justify-center">
                            <BadgeCheck className="w-4 h-4 text-white" />
                          </div>
                          <p className="text-sm font-semibold text-foreground">Vérification de sécurité</p>
                        </div>
                        <TurnstileCaptcha
                          onVerify={(isValid, token) => {
                            if (isValid && token) {
                              setIsCaptchaVerified(true);
                            } else {
                              setIsCaptchaVerified(false);
                            }
                          }}
                          onError={(error) => {
                            toast({
                              title: "Erreur CAPTCHA",
                              description: error,
                              variant: "destructive"
                            });
                          }}
                        />
                      </div>

                      <div className="flex items-start bg-muted/50 rounded-xl p-4">
                        <input
                          type="checkbox"
                          id="consent"
                          checked={formData.consent}
                          onChange={(e) =>
                            setFormData({ ...formData, consent: e.target.checked })
                          }
                          className="mt-1 mr-3 w-4 h-4 accent-primary"
                        />
                        <label htmlFor="consent" className="text-sm text-muted-foreground">
                          J'accepte que mes données soient traitées conformément à la
                          politique de confidentialité et au RGPD
                        </label>
                      </div>
                      <div className="flex gap-4">
                        <Button
                          variant="outline"
                          size="lg"
                          onClick={handlePreviousStep}
                          className="flex-1"
                          disabled={uploading}
                        >
                          Retour
                        </Button>
                        <Button
                          variant="cta"
                          size="lg"
                          onClick={handleSubmit}
                          className="flex-1"
                          disabled={uploading || !formData.specialty || !formData.cv || !formData.consent || !isCaptchaVerified || isBlocked}
                        >
                          {uploading ? (
                            <>
                              <span className="animate-pulse">Envoi en cours...</span>
                            </>
                          ) : (
                            'Envoyer ma candidature'
                          )}
                        </Button>
                      </div>

                      {/* Barre de progression avec étapes claires */}
                      {uploading && (
                        <div className="space-y-4 animate-fade-in bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl p-6 border border-primary/20">
                          {/* En-tête de progression */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <Loader2 className="w-5 h-5 text-primary animate-spin" />
                              <p className="text-sm font-semibold text-foreground">
                                Envoi en cours...
                              </p>
                            </div>
                            <span className="text-sm font-bold text-primary">
                              {uploadProgress}%
                            </span>
                          </div>

                          {/* Barre de progression globale */}
                          <Progress value={uploadProgress} className="h-2" />

                          {/* Étapes visuelles */}
                          <div className="grid grid-cols-4 gap-2 mt-4">
                            {[
                              { label: "Validation", threshold: 10, icon: CheckCircle2 },
                              { label: "Préparation", threshold: 30, icon: Clock },
                              { label: "Upload", threshold: 70, icon: Upload },
                              { label: "Finalisation", threshold: 100, icon: Check }
                            ].map((stage, index) => {
                              const isCompleted = uploadProgress >= stage.threshold;
                              const isCurrent = uploadProgress >= (index > 0 ? [10, 30, 70][index - 1] : 0) && uploadProgress < stage.threshold;
                              const Icon = stage.icon;
                              
                              return (
                                <div
                                  key={stage.label}
                                  className={`flex flex-col items-center gap-2 p-3 rounded-lg transition-all duration-300 ${
                                    isCompleted 
                                      ? 'bg-primary/10 border border-primary/30' 
                                      : isCurrent
                                      ? 'bg-primary/5 border border-primary/20 animate-pulse'
                                      : 'bg-muted/30 border border-transparent'
                                  }`}
                                >
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                                    isCompleted 
                                      ? 'bg-primary text-white' 
                                      : isCurrent
                                      ? 'bg-primary/20 text-primary'
                                      : 'bg-muted text-muted-foreground'
                                  }`}>
                                    <Icon className="w-4 h-4" />
                                  </div>
                                  <span className={`text-xs font-medium text-center ${
                                    isCompleted || isCurrent ? 'text-primary' : 'text-muted-foreground'
                                  }`}>
                                    {stage.label}
                                  </span>
                                  {isCompleted && (
                                    <CheckCircle2 className="w-3 h-3 text-success animate-fade-in" />
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Message de statut */}
                          <p className="text-xs text-center text-muted-foreground">
                            {uploadProgress < 10 ? "Vérification des informations..." :
                             uploadProgress < 30 ? "Préparation de votre fichier..." :
                             uploadProgress < 70 ? "Téléchargement de votre CV..." :
                             uploadProgress < 100 ? "Finalisation de l'envoi..." : 
                             "Candidature envoyée avec succès !"}
                          </p>
                        </div>
                      )}
                    </div>
                  )}


                  {step === 3 && (
                    <div className="text-center py-12">
                      <div className="relative inline-block mb-6">
                        <div className="absolute inset-0 bg-success/20 rounded-full blur-xl animate-pulse"></div>
                        <div className="relative w-24 h-24 mx-auto bg-gradient-to-br from-success to-success/80 rounded-full flex items-center justify-center shadow-xl">
                          <Check className="w-12 h-12 text-white" />
                        </div>
                      </div>
                      <h3 className="font-poppins font-black text-3xl bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-4">
                        Candidature envoyée !
                      </h3>
                      <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
                        Merci pour votre candidature. Notre équipe vous contactera
                        dans les 24h pour discuter de vos opportunités.
                      </p>
                      <Button
                        variant="cta"
                        size="lg"
                        onClick={() => {
                          setStep(1);
                          setFormData({
                            firstName: "",
                            lastName: "",
                            email: "",
                            phone: "",
                            countryCode: "+33",
                            specialty: "",
                            zone: "",
                            availability: "",
                            cv: null,
                            cvName: "",
                            consent: false,
                          });
                        }}
                      >
                        Nouvelle candidature
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SpontaneousApplication;